<template>
    <div class="presentation-step-nav-item">
        <div
            class="presentation-step-nav-header"
            role="tab">
            <div class="title">
                <i class="fas fa-fw fa-arrows-alt-v step-drag-handle"> </i>
                {{ stepIn.name }}
                <b-dropdown
                    class="ellipsis-nav presentation-step-context-menu"
                    variant="primary"
                    no-caret
                    right>
                    <template #button-content>
                        <i class="fas fa-fw fa-ellipsis-v"></i>
                    </template>
                    <b-dropdown-item
                        class="btn-edit nav-context-link-edit"
                        @click="editStepModal">
                        {{ $t('common.edit') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                        class="nav-context-edit-remove"
                        v-b-tooltip="$t('common.delete')"
                        @click="removeStep(stepIn)"
                        variant="inverse-danger">
                        <i class="btn-remove"></i>{{ $t('common.delete') }}
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>

        <draggable
            class="invisible-drop"
            :list="slides"
            ghost-class="ghost"
            group="slides"
            @change="orderChanged">
        </draggable>

        <b-list-group
            class="step-slides-group"
            :id="`collapse-${stepIn.id}`">
            <draggable
                :list="slides"
                group="slides"
                handle=".step-drag-handle"
                ghost-class="ghost"
                @change="orderChanged">
                <b-list-group-item
                    class="slide"
                    v-for="slide in slides"
                    :class="{ selected: isSelected(slide) }"
                    tag="div"
                    @click="selectSlide(slide)"
                    :key="slide.id">
                    <i class="fas fa-fw fa-arrows-alt-v step-drag-handle"></i>
                    {{ $t('presentation.slide') + ' ' + slide.order }}

                    <i
                        class="slide-type-icon fa"
                        :class="[getTypeIcon(slide.type)]">
                    </i>
                </b-list-group-item>
            </draggable>
        </b-list-group>

        <div class="new-slide">
            <b-button
                class="btn-add btn-add-slide"
                variant="primary"
                @click="openAddSlideModal">
                {{ $t('presentation.edit.add_slide') }}
            </b-button>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
export default {
    name: 'design-2021-presentation-step-nav-component',
    components: {
        draggable,
    },
    props: {
        stepIn: {
            type: Object,
            required: true,
        },
        selectedSlideId: {
            required: false,
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            slides: this.stepIn.slides.slice(),
            storageName: 'presentation-edit-selected-slide' + this.stepIn.presentation_id,
        };
    },

    mounted() {
        if (!localStorage.getItem(this.storageName)) {
            return;
        }

        const slideId = parseInt(localStorage.getItem(this.storageName));

        this.$emit('selected-slide', slideId);

        const slide = this.stepIn.slides.find((slide) => slide.id === slideId);

        if (slide) {
            this.isSelected(slide);
        }
    },

    methods: {
        selectSlide(slide) {
            localStorage.setItem(this.storageName, slide.id);
            this.$emit('selected-slide', slide.id);
            this.isSelected(slide);
            this.$forceUpdate();
        },
        orderChanged(target) {
            let x = 1;
            let blocks = [];

            for (const slide of this.slides) {
                blocks.push({
                    id: slide.id,
                    order: x,
                });
                x++;
            }

            const data = {
                step_id: this.stepIn.id,
                blocks: blocks,
            };
            this.axios.post(this.routes.presentationSlideOrder(), data).then(
                (response) => {
                    this.$emit('presentation-updated', response.data);
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        openAddSlideModal() {
            this.$emit('open-add-slide-modal', this.stepIn.id);
        },
        editStepModal() {
            this.$emit('open-edit-step-modal', this.stepIn);
        },
        removeStep() {
            this.alert
                .confirm(this.$t('presentation.edit.remove_step_title'), this.$t('presentation.edit.remove_step_body'))
                .then((confirm) => {
                    if (confirm) {
                        this.alert.saving();
                        this.axios.delete(this.routes.presentationRemoveStep(this.stepIn.id)).then(
                            (response) => {
                                this.$emit('presentation-updated', response.data);
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    }
                });
        },
        getTypeIcon(type) {
            switch (type) {
                case 1:
                    return 'fa-file-alt';
                case 2:
                    return 'fa-video';
                case 3:
                    return 'fa-bars';
                case 4:
                    return 'fa-list';
            }
        },

        isSelected(slide) {
            return this.selectedSlideId && slide.id === this.selectedSlideId;
        },
    },
    watch: {
        stepIn(newStep, oldStep) {
            this.slides = newStep.slides.slice();
        },
    },
};
</script>

<style scoped lang="scss">
.presentation-step-nav-item {
    background-color: $white;
    margin-bottom: $grid-gutter-width;
    border-radius: $border-radius-content;
}

.presentation-step-nav-header {
    background-color: $primary;
    color: $white;
    padding: map-get($grid-gutter-spacing, 'ten');
    border-top-left-radius: $border-radius-content;
    border-top-right-radius: $border-radius-content;

    .title {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: map-get($grid-gutter-spacing, 'five') map-get($grid-gutter-spacing, 'eight');
        font-weight: $font-weight-medium;
        font-size: $base-font-size;
        line-height: 1.5;

        ::v-deep .ellipsis-nav {
            margin-left: auto;

            button.dropdown-toggle {
                padding: 0;
                color: $white !important;
            }
        }
    }
}

.step-slides-group {
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
}

.slide {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: map-get($grid-gutter-spacing, 'five') map-get($grid-gutter-spacing, 'eight');
    padding: map-get($grid-gutter-spacing, 'ten') map-get($grid-gutter-spacing, 'twenty-one');
    border: none;
    border-bottom: 3px dotted $border-color;

    .slide-type-icon {
        margin-left: auto;
    }

    &:hover,
    &.selected {
        color: $primary;
        font-weight: $font-weight-medium;

        .slide-type-icon {
            color: $primary;
        }
    }

    &:hover {
        cursor: pointer;
    }
}

.new-slide {
    padding-left: map-get($grid-gutter-spacing, 'twenty-one');
    padding-bottom: map-get($grid-gutter-spacing, 'fifteen');

    button {
        font-size: $default-font-size;
        line-height: 1;
    }
}
</style>
